import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {MatDialog} from "@angular/material/dialog";
import {CdkDragDrop} from "@angular/cdk/drag-drop";
import {ToastrService} from "ngx-toastr";
import {TranslocoService} from "@ngneat/transloco";
import {MatTableDataSource} from '@angular/material/table';
import {Compbaser} from "../../../../components/compbaser";
import {AppService, ContainerService, ImportService, SceneAddService, StoreSelectorsService} from "../../../../services";
import {EnumWizardUpgradeToasts, IAppSettingsWrap, IPlayerProps, IResource, ISceneAddBlock, ISceneBlock, IUser} from "../../../../interfaces";
import {createResourceUrl, createScenePreviewUrl, DRAG_ITEM_SIZE, getResourceFormat, isResourceItem, popupWindow} from "../../../../common";
import {DialogGetAssetComponent, IDialogGetAsset} from '../../../../components/dialog-get-asset/dialog-get-asset.component';
import {staticAnimationTemplates} from 'assets/accountTemplates/animation-scene-constants';
import {take} from 'rxjs/operators';
import * as _ from "lodash";
import {Lib} from "../../../../../libs/Lib";

interface IAnimationSticker {
    businessId: number;
    sceneId: string;
    resolution: string;
    playlistIncluded: boolean;
    fileName: string;
    banner: boolean;
}

interface IAnimationPlayer {
    name: string;
    type: string;
    isPlaylist: boolean;
    id: string;
    src: string;
    image: string;
    playlistId?: string;
}

@Component({
    selector: 'app-scene-add-animation',
    templateUrl: './scene-add-animation.component.html',
    styleUrls: ['./scene-add-animation.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SceneAddAnimationComponent extends Compbaser implements OnInit {

    @ViewChild("downloadModal", {read: TemplateRef}) downloadModal: TemplateRef<any>;
    @ViewChild("animationPlayers", {read: TemplateRef}) animationPlayers: TemplateRef<any>;

    addBlockType: ISceneAddBlock = 'BLOCK_ANIMATION';
    isFullFeatures: boolean;
    coreAppData: IAppSettingsWrap;
    hiddenPanel = true;
    stickers = [];
    currentStickers: IAnimationSticker[] = [];
    resourceName: string;
    activeBlockId;
    loadingData = false;
    dataSource: MatTableDataSource<IAnimationPlayer>;
    displayedColumns: string[] = ['name', 'type', 'image', 'swap'];
    labelCount = 0;
    selectedItem: IAnimationSticker;
    selectedPlayers: ISceneBlock[] = [];
    animationSceneData: IPlayerProps;
    swappedIds = [];
    isMouseOnScene = false;
    dragEvent: CdkDragDrop<any>;
    animationViewType = 'resource';
    throttle = 300;
    scrollDistance = 2;
    initialCount = 30;
    verticalMoreCount = 20;
    user: IUser;

    readonly dragItemSize = DRAG_ITEM_SIZE;

    constructor(
        private translocoService: TranslocoService,
        public storeSelector: StoreSelectorsService,
        private containerService: ContainerService,
        protected override dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        private appService: AppService,
        private toastr: ToastrService,
        private sceneAddService: SceneAddService,
        private importService: ImportService) {
        super(dialog);
    }

    ngOnInit() {
        this.cod(
            this.storeSelector.getUser()
                .subscribe((i_user: IUser) => {
                    this.user = i_user;
                })
        );
        this.cod(
            this.storeSelector.isFullFeatures().subscribe((v) => {
                this.isFullFeatures = v;
            })
        );
        this.cod(
            this.storeSelector.getCoreData()
                .subscribe((i_coreData: IAppSettingsWrap) => this.coreAppData = i_coreData)
        );
        this.cod(
            this.importService.progress$.subscribe(resourceName => {
                this.resourceName = String(resourceName);
                this.cdr.markForCheck();
            })
        );
        this.cod(
            this.containerService.isMouseOnScene.subscribe((v) => {
                this.isMouseOnScene = v;
            })
        );
        this.composeStickers();
        this.sceneAddService.setBlockType(this.addBlockType);
        this.dataSource = new MatTableDataSource([]);

        setTimeout(() => {
            this.hiddenPanel = false;
            this.cdr.markForCheck();
        }, 500);
    }

    composeStickers(event = null) {
        if (event != null) {
            const array = ['resource', 'playlist', 'banner'];
            this.animationViewType = array[event.index];
        }

        switch (this.animationViewType) {
            case 'resource': {
                const allStickers = staticAnimationTemplates.filter((v) => v.playlistIncluded === false && v.banner === false);
                this.currentStickers = allStickers.slice(0, this.initialCount);

                break;
            }
            case 'playlist': {
                const allStickers = staticAnimationTemplates.filter((v) => v.playlistIncluded === true);
                this.currentStickers = allStickers.slice(0, this.initialCount);
                break;
            }
            case 'banner': {
                const allStickers = staticAnimationTemplates.filter((v) => v.banner === true);
                this.currentStickers = allStickers.slice(0, this.initialCount);
                break;
            }
        }

        if (event) {
            this.cdr.detectChanges();
        }
    }

    /** Load more stickers when scroll down */
    loadMoreStickers() {
        const cnt = this.currentStickers.length;
        switch (this.animationViewType) {
            case 'resource': {
                const allStickers = staticAnimationTemplates.filter((v) => v.playlistIncluded === false && v.banner === false);
                if (cnt < allStickers.length) {
                    this.currentStickers.push(...allStickers.slice(cnt, cnt + this.verticalMoreCount));
                }
                break;
            }
            case 'playlist': {
                const allStickers = staticAnimationTemplates.filter((v) => v.playlistIncluded === true);
                if (cnt < allStickers.length) {
                    this.currentStickers.push(...allStickers.slice(cnt, cnt + this.verticalMoreCount));
                }
                break;
            }
            case 'banner': {
                const allStickers = staticAnimationTemplates.filter((v) => v.banner === true);
                if (cnt < allStickers.length) {
                    this.currentStickers.push(...allStickers.slice(cnt, cnt + this.verticalMoreCount));
                }
                break;
            }
        }
        this.cdr.detectChanges();
    }

    previewAnimation(event: IAnimationSticker) {
        popupWindow({url: createScenePreviewUrl(this.coreAppData, event.businessId, event.sceneId, this.isFullFeatures), title: '_blank', w: 480, h: 270}, 'yes', 'yes');
    }

    previewAnimationBanner(event: IAnimationSticker) {
        if (this.animationViewType === 'banner') {
            popupWindow({url: createScenePreviewUrl(this.coreAppData, event.businessId, event.sceneId, this.isFullFeatures), title: '_blank', w: 480, h: 87}, 'yes', 'yes');
        } else {
            popupWindow({url: createScenePreviewUrl(this.coreAppData, event.businessId, event.sceneId, this.isFullFeatures), title: '_blank', w: 480, h: 270}, 'yes', 'yes');
        }
    }

    addSticker(event: IAnimationSticker) {
        this.dragEvent = null;
        this.selectedItem = event;
        this.showSwapDialog().catch();
    }

    dropSticker(event: CdkDragDrop<any>) {
        if (event.isPointerOverContainer) return;
        if (!this.isMouseOnScene) {
            this.toastr.warning(this.translocoService.translate('toast.notDropArea', {}, this.translocoService.getActiveLang()));
            return;
        }
        this.selectedItem = event.item.data;
        this.dragEvent = event;
        this.showSwapDialog().catch();
    }

    async showSwapDialog() {
        this.loadingData = this.animationViewType !== 'banner';
        this.swappedIds = [];
        const dialogRef = this.dialog.open(this.animationPlayers, {
            width: '600px',
            data: {},
            disableClose: true
        });

        this.cod(
            dialogRef.afterClosed().subscribe(result => {
                this.selectedItem = null;
                this.cdr.detectChanges();
            })
        );

        try {
            // get players of animation sticker
            this.animationSceneData = await this.importService.getRemoteScene(this.selectedItem.businessId, this.selectedItem.sceneId);
            this.selectedPlayers = this.animationSceneData.Player.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player;
        } catch {
            this.toastr.error(this.translocoService.translate('toast.failedImportSticker', {}, this.translocoService.getActiveLang()));
            dialogRef.close();
            return;
        }

        if (!this.selectedPlayers?.length) {
            this.dataSource = new MatTableDataSource([]);
            return;
        }

        const items: IAnimationPlayer[] = [];
        this.labelCount = 0;

        // extract swappable resources from players
        for (const _player of this.selectedPlayers) {
            const isResource = isResourceItem(_player);
            const isPlaylist = _player.$.player === this.storeSelector.blockLabels.PLAYLIST.id;
            if (!isResource && !isPlaylist) {
                this.labelCount += 1;
                continue;
            }

            const item: IAnimationPlayer = {
                name: _player.$.label,
                type: _player.$.player,
                isPlaylist,
                id: _player.$.id,
                src: isResource ? _player.Data[0]?.Resource?.[0]?.$?.resource : "",
                image: null
            };

            if (isResource) {
                if (!item.src) continue;
                const resourceInfo = await this.importService.getRemoteResource(this.selectedItem.businessId, item.src);
                item.name = resourceInfo.resource_name;
                item.type = resourceInfo.resource_type;
                if (!this.selectedItem) return;
                item.image = createResourceUrl(this.coreAppData, this.selectedItem.businessId, item.src, item.type);
                items.push(item);
            }

            // if player is playlist, get resources of playlist
            if (isPlaylist) {
                const pages = _player.Data[0].Playlist[0].Page || [];
                for (const _page of pages) {
                    if (_page.$.type === 'scene') continue;

                    item.src = _page.Player[0].Data[0].Resource[0].$.resource;
                    const resourceInfo = await this.importService.getRemoteResource(this.selectedItem.businessId, item.src);
                    item.name = resourceInfo.resource_name;
                    item.type = resourceInfo.resource_type;
                    item.playlistId = _player.$.id;
                    item.id = _page.Player[0].$.id;
                    if (!this.selectedItem) return;
                    item.image = createResourceUrl(this.coreAppData, this.selectedItem.businessId, item.src, item.type);
                    items.push(_.cloneDeep(item));
                }
            }
        }

        // create table with swappable items. If it includes playlist, group by playlist.
        if (this.selectedItem.playlistIncluded) {
            this.dataSource.data = this.convertDataGroupByPlaylist(items);
        } else {
            this.dataSource.data = items;
        }
        this.loadingData = false;
        this.cdr.detectChanges();
    }

    async importAnimationSticker() {
        if (!this.animationSceneData || !this.selectedPlayers.length) return;
        if (Lib.IsFreeAccount(this.user) || Lib.IsAccessBlocked(this.user)) {
            this.appService.openWizardUpgradeDialog(EnumWizardUpgradeToasts.UPGRADE_TO_USE_FEATURE);
            return;
        }
        this.dialog.open(this.downloadModal, {
            height: '400px',
            width: '400px',
            panelClass: 'transparentDialog',
            data: {},
            disableClose: true
        });

        try {
            // import animation players by skipping the swapped one
            await this.importService.importPlayers(this.selectedPlayers, this.selectedItem.businessId, true, true, "", this.swappedIds);

            // replace scene players with new items
            const sceneData = _.cloneDeep(this.animationSceneData);
            sceneData.Player.Data[0].Scene[0].Players[0].Player = this.selectedPlayers;

            // import only scene data
            const animationName = `_animation_${this.selectedItem.businessId}_${this.selectedItem.sceneId}`;
            this.activeBlockId = await this.importService.importPrebuiltScene(sceneData, animationName, true);
            if (!this.activeBlockId) {
                this.toastr.error(this.translocoService.translate('toast.failedImportSticker', {}, this.translocoService.getActiveLang()));
                return;
            }

            // add animation sticker to root scene
            this.sceneAddService.setActiveBlockId(this.activeBlockId);
            if (this.dragEvent) {
                this.sceneAddService.dropBlock(this.dragEvent);
            } else {
                this.sceneAddService.addBlock();
            }
            this.cdr.detectChanges();

        } catch (err) {
            console.log(err);
        } finally {
            setTimeout(() => {
                this.dialog.closeAll();
            }, 1000);
        }
    }

    openAssociateDialog(row) {
        const data: IDialogGetAsset = {mode: "RESOURCE:ALL", multiSelect: false};
        const dialogRefVal = this.dialog.open(DialogGetAssetComponent, {
            width: '70%',
            panelClass: 'custom-dlg-container',
            data
        });
        this.cod(
            dialogRefVal.afterClosed()
                .pipe(take(1))
                .subscribe((dialogGetAsset: IDialogGetAsset) => {
                    if (!dialogGetAsset || !this.selectedItem) return;

                    // check if new resource is video type
                    // const newResourceType = dialogGetAsset.sourceData.resource_type;
                    // if (!RESOURCE_IMAGE_TYPES.includes(newResourceType)) {
                    //     this.toastr.warning(this.translocoService.translate('toast.invalidResource', {}, this.translocoService.getActiveLang()));
                    //     return;
                    // }

                    this.swapResource(dialogGetAsset.sourceData, row);
                    this.cdr.detectChanges();
                })
        );
    }

    private swapResource(newResource: IResource, row: IAnimationPlayer) {
        if (!newResource) return;

        const playerId = row.id;
        const player: ISceneBlock = this.selectedPlayers.find((_player) => _player.$.id === (row.isPlaylist ? row.playlistId : playerId));
        if (!player) return;

        let playerProps: ISceneBlock;
        let isImage = true;
        if (newResource.resource_type === 'svg') {
            playerProps = _.cloneDeep(this.storeSelector.blockLabels.SVG.template);
        } else if (["mp4", "mov", "m4v", "3gp"].indexOf(newResource.resource_type) > -1) {
            playerProps = _.cloneDeep(this.storeSelector.blockLabels.VIDEO.template);
            isImage = false;
        } else {
            playerProps = _.cloneDeep(this.storeSelector.blockLabels.IMAGE.template);
        }

        // if it is inside of playlist, replace the playlist item
        if (row.isPlaylist) {
            const pageToSwap = player.Data[0].Playlist[0].Page.find((_player) => _player.Player[0].$.id === playerId);
            if (!pageToSwap) return;

            pageToSwap.Player[0].Data[0].Resource[0].$.resource = newResource.resource_id;
            pageToSwap.Player[0].$.player = playerProps.$.player;
            pageToSwap.$.page = newResource.resource_name || playerProps.$.label;
        } else {
            player.$.player = playerProps.$.player;
            player.$.label = newResource.resource_name || playerProps.$.label;
            player.Data[0].Resource = playerProps.Data[0].Resource;
            player.Data[0].Resource[0].$.resource = newResource.resource_id;
        }

        // remove background effect when swap resource
        if ("Background" in player.Data[0]) {
            delete player.Data[0]["Background"];
        }

        if (!this.swappedIds.includes(playerId)) {
            this.swappedIds.push(playerId);
        }

        // replace the model swap image
        if (isImage) {
            row.image = createResourceUrl(this.coreAppData, null, null, null, newResource);
        } else {
            row.image = getResourceFormat(newResource).url;
        }
    }

    private convertDataGroupByPlaylist(items: IAnimationPlayer[] = []) {
        const newItems = [];
        const resources = items.filter((_item) => !_item.isPlaylist);
        if (resources.length > 0) {
            newItems.push(
                {
                    groupName: "Resources",
                    isGroupBy: true
                },
                ...resources
            );
        }
        const groupByPlaylist = _.groupBy(items.filter((_item) => _item.isPlaylist), _item => _item.playlistId);
        Object.keys(groupByPlaylist).forEach((key, index) => {
            newItems.push(
                {
                    groupName: `Playlist ${index + 1}`,
                    isGroupBy: true
                },
                ...groupByPlaylist[key]
            );
        });
        return newItems;
    }

    isGroup(index, item): boolean {
        return item.isGroupBy;
    }

    onClose() {
        this.dialog.closeAll();
    }

    override destroy() {
        this.sceneAddService.setActiveBlockId(null);
        super.destroy();
    }
}
